import { IPermissions } from '../common/permissions.types';
import { EFeatureFlag } from '../constants/enums';
import {
    companyLevelNavItems,
    pageFeatureFlagMap,
    paymentLinkProjectNavItems,
    portalLevelNavItems,
    portalLevelStoreNavItems,
    projectLevelNavItems
} from '../design-system/AcSidebar/constants';
import { EOffersRoute } from '../design-system/AcSidebar/pages';
import { ESideBarContext, SideBarGroup, SideBarItem } from '../design-system/AcSidebar/types';

import {
    buildPermissionsMatcher,
    resolveFeatureFlaggedPermissions
} from './permissionsUtil';

// Flatten sidebar groups into a single list of items
const flattenItems = (groups: SideBarGroup[]): SideBarItem[] => groups.flatMap(group => group.items);

// Legacy path aliases
const PATH_ALIASES: Record<string, string> = {
    '/analytics': '/statistics'
};

// Routes that don't require permission checks
const UNRESTRICTED_ROUTES: string[] = [
    '/login',
    '/sign-up',
    '/init-project',
    '/welcome',
    '/profile'
];

// Remove known context prefixes from a route
const stripContextPrefix = (path: string): string => {
    const prefixPatterns = [
        /^\/project\/[^/]+\/(.+)/,
        /^\/portal\/[^/]+\/(.+)/,
        /^\/store\/[^/]+\/(.+)/
    ];

    for (const pattern of prefixPatterns) {
        const match = path.match(pattern);
        if (match) {
            return '/' + match[1];
        }
    }

    return path;
};

const getRouteContextFromPath = (path: string): ESideBarContext => {
    if (path.startsWith('/project/')) return ESideBarContext.PROJECT;
    if (path.startsWith('/portal/')) return ESideBarContext.PORTAL;
    if (path.startsWith('/store/')) return ESideBarContext.PORTAL_LEVEL_STORE;
    return ESideBarContext.COMPANY;
};

// Build the set of sidebar items for a given context and shaping inputs
const buildContextItems = (
    context: ESideBarContext,
    options: {
        featureFlags?: Record<EFeatureFlag, any>;
        currentProjectSubType?: string | null;
        hasSingleProject?: boolean;
    }
): SideBarItem[] => {
    const { featureFlags, currentProjectSubType, hasSingleProject } = options;

    let groups: SideBarGroup[];
    switch (context) {
        case ESideBarContext.PROJECT: {
            const initialGroups = currentProjectSubType === 'paymentLinks'
                ? paymentLinkProjectNavItems(!!hasSingleProject)
                : projectLevelNavItems;

            if (!featureFlags) {
                groups = initialGroups;
                break;
            }

            // Apply feature flag gating like the sidebar
            const filtered = initialGroups
                .map(group => ({
                    ...group,
                    items: group.items.filter(item => {
                        const requiredFlag = pageFeatureFlagMap[item.title as keyof typeof pageFeatureFlagMap];
                        if (!requiredFlag) return true;
                        // treat any truthy value as enabled (can be boolean or object)
                        return Boolean(featureFlags[requiredFlag]);
                    })
                }))
                .filter(group => group.items.length > 0);

            groups = filtered;
            break;
        }
        case ESideBarContext.PORTAL:
            groups = portalLevelNavItems;
            break;
        case ESideBarContext.PORTAL_LEVEL_STORE:
            groups = portalLevelStoreNavItems;
            break;
        case ESideBarContext.COMPANY:
        default:
            groups = companyLevelNavItems;
    }

    return flattenItems(groups);
};

export const checkRouteAccess = (
    routePath: string,
    permissions: IPermissions | null,
    currentProjectId?: string,
    options?: {
        featureFlags?: Record<EFeatureFlag, any>;
        currentProjectSubType?: string | null;
        hasSingleProject?: boolean;
    }
): boolean => {
    // Skip permission check for unrestricted routes
    const isPublicRoute = UNRESTRICTED_ROUTES.some(publicRoute =>
        routePath.startsWith(publicRoute)
    );
    if (isPublicRoute) return true;

    // Deny access if no permissions are provided
    if (!permissions) return false;

    // Determine context and normalize the route path
    const context = getRouteContextFromPath(routePath);
    const cleanRoutePath = stripContextPrefix(routePath);

    // Build context-specific items shaped identically to the sidebar
    const contextItems = buildContextItems(context, options || {});

    // Find sidebar item by route or alias
    const topSegment = '/' + (cleanRoutePath.split('/')[1] || '');
    const resolvedKey = PATH_ALIASES[topSegment] || topSegment;
    const sidebarItem = contextItems.find(item => '/' + item.url === cleanRoutePath)
        || contextItems.find(item => '/' + item.url.split('/')[0] === resolvedKey);

    // BuyOneGetMany route is gated by DASHBOARD_ROLLING_OFFER_1N_ENABLED. The sidebar item is
    // already filtered out of contextItems when the flag is off, so the lookup above misses and
    // the unknown-route fallback would otherwise allow direct URL access. Mirror the
    // buildContextItems FF-filter scope (project context only) to avoid false-denying store and
    // company contexts that do not load feature flags.
    const isBuyOneGetManyRoute = topSegment === '/' + EOffersRoute.BUY_ONE_GET_MANY;
    if (isBuyOneGetManyRoute && context === ESideBarContext.PROJECT) {
        const buyOneGetManyFlagEnabled = Boolean(
            options?.featureFlags?.[EFeatureFlag.DASHBOARD_ROLLING_OFFER_1N_ENABLED]
        );
        if (!buyOneGetManyFlagEnabled) return false;
    }

    // Allow access if no matching item (unknown route) or no permissions defined for the item
    if (!sidebarItem || !sidebarItem.permissions?.length) return true;

    // Special case: Portal context routes don't require permission checking
    // This matches the sidebar behavior where portal-level items are always visible
    if (context === ESideBarContext.PORTAL) {
        return true;
    }

    const requiredPermissions = resolveFeatureFlaggedPermissions(
        sidebarItem.permissions,
        sidebarItem.featureFlaggedPermissions,
        options?.featureFlags
    );
    if (requiredPermissions.length === 0) {
        return false;
    }
    const matchesRequiredPermissions = buildPermissionsMatcher(
        requiredPermissions,
        sidebarItem.matchAnyPermission
    );

    // Company-level access (no currentProjectId): check any project for required permissions
    if (!currentProjectId) {
        return Object.values(permissions).some(projectPermissions =>
            matchesRequiredPermissions(permission => Object.hasOwn(projectPermissions, permission))
        );
    }

    // Project-level access: check current project’s permissions
    const currentProjectPermissions = permissions[currentProjectId];
    if (!currentProjectPermissions) return false;

    // Special case: Localization requires offers:manage (match sidebar logic)
    if (sidebarItem.title === 'Localization') {
        return Array.isArray(currentProjectPermissions.offers)
            ? currentProjectPermissions.offers.includes('manage')
            : false;
    }

    // Check each required permission in the current project for view/manage
    return matchesRequiredPermissions(permission => {
        const actions = currentProjectPermissions[permission];
        return Array.isArray(actions) && (actions.includes('view') || actions.includes('manage'));
    });
};
